import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  sessionsApi,
  SESSION_TYPE_LABELS,
  type SessionFilters,
  type SessionSummary,
} from '@/api/sessions';
import { KpiCard } from '@/components/KpiCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  PERIOD_OPTIONS,
  SOURCE_OPTIONS,
  currentCursor,
  initialPager,
  isServerAutocreated,
  nextPage,
  pageIndex,
  pageRangeLabel,
  periodRange,
  prevPage,
  sourceBadge,
  type Pager,
} from '@/lib/businessSessions';
import {
  athleteLabel,
  formatDateTime,
  formatDistance,
  formatDuration,
  formatDurationMs,
  formatSpeedKmh,
  modalityLabel,
} from '@/lib/sessionFormat';
import { SPORT_CATALOG, sportFullLabel } from '@/lib/sportCatalog';

/// Painel de negócio (#196) — sessões esportivas gravadas (solo / downwind /
/// corrida) direto do Postgres. Gate: `verified_organizer` (super-admin),
/// aplicado no AuthGuard em App.tsx e reforçado no backend.
///
/// #939 — sem o teto de 200: os KPIs vêm do `/api/sessions/summary`, agregado
/// no servidor sobre o período inteiro, e não mudam ao paginar. A tabela
/// pagina por cursor (keyset) e todos os filtros descem pro servidor, inclusive
/// tipo, modalidade e origem, que antes eram filtrados no cliente sobre a
/// janela. O "N" do "1–50 de N" é o total do resumo, com o mesmo filtro.

const PAGE_SIZE = 50;

export function BusinessPage() {

  const [period, setPeriod] = useState<string>('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [athleteInput, setAthleteInput] = useState('');
  const [athlete, setAthlete] = useState('');

  // Memoiza por período: sem isso, `Date.now()` recomputaria a cada render →
  // nova string ISO → nova queryKey → refetch infinito. Recalcula só quando o
  // operador troca o período ou as datas do personalizado.
  const range = useMemo(
    () => periodRange(period, { from: customFrom, to: customTo }, Date.now()),
    [period, customFrom, customTo],
  );

  const filters = useMemo<SessionFilters | null>(
    () =>
      range == null
        ? null
        : {
            ...range,
            type: typeFilter || undefined,
            sport: sportFilter || undefined,
            source: sourceFilter || undefined,
            athlete: athlete || undefined,
          },
    [range, typeFilter, sportFilter, sourceFilter, athlete],
  );
  const filtersKey = JSON.stringify(filters);

  // A pilha de cursores pertence a UM filtro. Guardada junto com a chave dele:
  // trocar filtro devolve a primeira página já no mesmo render, sem um fetch
  // intermediário com o cursor velho e o filtro novo.
  const [pagerState, setPagerState] = useState<{ key: string; pager: Pager }>({
    key: filtersKey,
    pager: initialPager(),
  });
  const pager = pagerState.key === filtersKey ? pagerState.pager : initialPager();
  const cursor = currentCursor(pager);

  const summary = useQuery({
    queryKey: ['business-sessions-summary', filters],
    queryFn: () => sessionsApi.summary(filters!),
    enabled: filters != null,
  });

  const page = useQuery({
    queryKey: ['business-sessions', filters, cursor],
    queryFn: () => sessionsApi.list({ ...filters!, cursor, limit: PAGE_SIZE }),
    enabled: filters != null,
    // Mantém a página anterior na tela enquanto a próxima carrega, em vez de
    // piscar o spinner a cada clique.
    placeholderData: keepPreviousData,
  });

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  async function handleExport() {
    if (!filters) return;
    setExporting(true);
    setExportError(null);
    try {
      await sessionsApi.exportCsv(filters);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Falha ao exportar.');
    } finally {
      setExporting(false);
    }
  }

  const rows = page.data?.items ?? [];
  const s = summary.data;

  return (
    <div>
      {/* #936 — "← Eventos", e-mail e Sair foram pro menu lateral. Ficam as
          ações da página: o CSV do período (#939) e o atalho "Crescimento",
          par natural desta página (protótipo). */}
      <header className="border-b">
        <div className="container mx-auto py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-lg sm:text-xl font-semibold">Negócio</h1>
          <div className="flex items-center gap-2 text-sm">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || filters == null}
            >
              {exporting ? (
                <>
                  <Spinner className="h-3.5 w-3.5" /> Exportando…
                </>
              ) : (
                'Exportar CSV do período'
              )}
            </Button>
            {/* #525 — crescimento da base (mesmo gate; a rota reforça no AuthGuard) */}
            <Button asChild variant="outline" size="sm">
              <Link to="/business/growth">Crescimento</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* max-w-7xl: com Kite point e a etiqueta de relógio, a tabela não cabe
          mais no 6xl e a coluna Status ficava cortada atrás da rolagem. */}
      <main className="container mx-auto py-6 sm:py-10 space-y-6 max-w-7xl">
        {exportError && (
          <p className="text-sm text-destructive" role="alert">
            Erro ao exportar CSV. {exportError}
          </p>
        )}

        {/* ─── Filtros ─────────────────────────────────────────────── */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="filter-period">Período</Label>
                <Select
                  id="filter-period"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-type">Tipo</Label>
                <Select
                  id="filter-type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">Todos os tipos</option>
                  {Object.entries(SESSION_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-sport">Modalidade</Label>
                <Select
                  id="filter-sport"
                  value={sportFilter}
                  onChange={(e) => setSportFilter(e.target.value)}
                >
                  <option value="">Todas</option>
                  {SPORT_CATALOG.map((sp) => (
                    <option key={sp.slug} value={sp.slug}>
                      {sportFullLabel(sp.slug)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-source">Origem</Label>
                <Select
                  id="filter-source"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <option value="">Todas</option>
                  {SOURCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
              {/* Busca livre (nome, e-mail ou UID) precisa de largura: em 1/5 da
                  linha o placeholder já saía cortado. */}
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="filter-athlete">Atleta</Label>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setAthlete(athleteInput.trim());
                  }}
                  className="flex gap-2"
                >
                  <Input
                    id="filter-athlete"
                    type="search"
                    value={athleteInput}
                    onChange={(e) => setAthleteInput(e.target.value)}
                    placeholder="Nome, e-mail ou UID"
                    className="flex-1 min-w-0"
                    autoComplete="off"
                  />
                  <Button type="submit" variant="outline" className="shrink-0">
                    Filtrar
                  </Button>
                </form>
              </div>
            </div>

            {period === 'custom' && (
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="filter-from">De</Label>
                  <Input
                    id="filter-from"
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="filter-to">Até (inclusive)</Label>
                  <Input
                    id="filter-to"
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </div>
                {range == null && (
                  <p className="text-xs text-muted-foreground pb-2">
                    Escolha as duas datas, com o início antes do fim.
                  </p>
                )}
              </div>
            )}

            {athlete && (
              <button
                type="button"
                onClick={() => {
                  setAthleteInput('');
                  setAthlete('');
                }}
                className="text-xs text-muted-foreground hover:underline"
              >
                Limpar filtro de atleta ({athlete})
              </button>
            )}
          </CardContent>
        </Card>

        {/* ─── KPIs ────────────────────────────────────────────────── */}
        {summary.isError ? (
          <div className="rounded-md border border-destructive/40 p-4 text-sm">
            <span className="text-destructive">Erro ao carregar os indicadores. </span>
            <Button variant="outline" size="sm" className="ml-2" onClick={() => summary.refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard label="Sessões no período" value={kpi(s?.total)} />
            <KpiCard label="Atletas ativos" value={kpi(s?.activeAthletes)} />
            <KpiCard label="Em andamento" value={kpi(s?.ongoing)} />
            <KpiCard label="Finalizadas" value={kpi(s?.finished)} />
            <KpiCard
              label="Distância total"
              value={s ? formatDistance(s.totalDistanceMeters) : '…'}
            />
            <KpiCard
              label="Duração média"
              value={s ? (s.avgDurationMs != null ? formatDurationMs(s.avgDurationMs) : '-') : '…'}
            />
          </div>
        )}
        <p className="text-xs text-muted-foreground -mt-3">
          Indicadores somados no servidor sobre o período inteiro, com os mesmos filtros da
          tabela. Não mudam ao paginar.
        </p>

        {/* ─── Tabela ──────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Sessões</CardTitle>
            {page.isSuccess && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {pageRangeLabel(pageIndex(pager), PAGE_SIZE, rows.length, s?.total)} · ordenado
                por início
              </span>
            )}
          </CardHeader>
          <CardContent>
            {filters == null && (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Escolha o período personalizado pra ver as sessões.
              </div>
            )}

            {page.isLoading && (
              <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Carregando sessões…
              </div>
            )}

            {page.isError && (
              <div className="py-8 text-center">
                <p className="text-sm text-destructive">
                  Erro ao carregar sessões.{' '}
                  {page.error instanceof Error ? page.error.message : ''}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => page.refetch()}
                >
                  Tentar novamente
                </Button>
              </div>
            )}

            {page.isSuccess && rows.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Nenhuma sessão nesse período/filtro.
              </div>
            )}

            {page.isSuccess && rows.length > 0 && (
              <div className={page.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
                <SessionsTable rows={rows} />
              </div>
            )}

            {page.isSuccess && (rows.length > 0 || pageIndex(pager) > 0) && (
              <div className="flex items-center justify-end gap-2 pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={pageIndex(pager) === 0 || page.isFetching}
                  onClick={() => setPagerState({ key: filtersKey, pager: prevPage(pager) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!page.data?.nextCursor || page.isFetching}
                  onClick={() =>
                    setPagerState({ key: filtersKey, pager: nextPage(pager, page.data?.nextCursor) })
                  }
                >
                  Próxima
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

const kpiFmt = new Intl.NumberFormat('pt-BR');
function kpi(n: number | undefined): string {
  return n == null ? '…' : kpiFmt.format(n);
}

function SessionsTable({ rows }: { rows: SessionSummary[] }) {
  const navigate = useNavigate();
  return (
    <div className="overflow-x-auto -mx-2 sm:mx-0">
      <table className="w-full text-sm min-w-[960px]">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 px-2 font-medium">Início</th>
            <th className="py-2 px-2 font-medium">Atleta</th>
            <th className="py-2 px-2 font-medium">Tipo</th>
            <th className="py-2 px-2 font-medium">Modalidade</th>
            <th className="py-2 px-2 font-medium">Kite point</th>
            <th className="py-2 px-2 font-medium text-right">Duração</th>
            <th className="py-2 px-2 font-medium text-right">Distância</th>
            <th className="py-2 px-2 font-medium text-right">Top speed</th>
            <th className="py-2 px-2 font-medium text-right">Pontos</th>
            <th className="py-2 px-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const badge = sourceBadge(s.source);
            // Shell do servidor (#638): tipo/modalidade são default, não
            // declaração. "-" em vez de "Freeride · Kite" inventados.
            const undeclared = isServerAutocreated(s.source);
            return (
              // Linha inteira clicável → replay. role/tabIndex + onKeyDown mantêm
              // acesso por teclado (Enter/Espaço), já que não é um <a> nativo.
              <tr
                key={s.id}
                onClick={() => navigate(`/business/sessions/${s.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/business/sessions/${s.id}`);
                  }
                }}
                role="link"
                tabIndex={0}
                className="border-b last:border-0 cursor-pointer hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
              >
                <td className="py-2 px-2 whitespace-nowrap text-primary">
                  {formatDateTime(s.startTime)}
                </td>
                <td className="py-2 px-2 max-w-[160px] truncate" title={s.userId}>
                  <AthleteCell displayName={s.displayName} userId={s.userId} />
                </td>
                <td className="py-2 px-2 whitespace-nowrap">
                  {undeclared ? '-' : (SESSION_TYPE_LABELS[s.sessionType] ?? s.sessionType)}
                  {badge && (
                    // Origem, não status: relógio e shell do servidor contam
                    // como qualquer sessão (sem quarentena desde 14/10/2026).
                    <span
                      className="ml-1.5 text-[10px] rounded px-1.5 py-0.5 bg-sky-500/15 text-sky-800 dark:text-sky-300 border border-sky-500/30"
                      title={
                        undeclared
                          ? 'Criada pelo servidor: o app não avisou o início da sessão, então tipo e modalidade não foram declarados'
                          : 'Gravada no relógio'
                      }
                    >
                      {badge}
                    </span>
                  )}
                </td>
                <td className="py-2 px-2 whitespace-nowrap">
                  {undeclared ? '-' : modalityLabel(s.sessionType, s.category)}
                </td>
                <td className="py-2 px-2 max-w-[140px] truncate" title={s.kitePointId ?? undefined}>
                  {s.kitePointName ?? s.kitePointId ?? '-'}
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap tabular-nums">
                  {formatDuration(s.startTime, s.endTime)}
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap tabular-nums">
                  {formatDistance(s.totalDistanceMeters)}
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap tabular-nums">
                  {formatSpeedKmh(s.maxSpeedKnots)}
                </td>
                <td className="py-2 px-2 text-right whitespace-nowrap tabular-nums">
                  {s.gpsPointsRecorded}
                </td>
                <td className="py-2 px-2">
                  <StatusChip status={s.status} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AthleteCell({
  displayName,
  userId,
}: {
  displayName: string | null;
  userId: string;
}) {
  const { label, isFallback } = athleteLabel(displayName, userId);
  return (
    <span className={isFallback ? 'font-mono text-xs text-muted-foreground' : 'text-sm'}>
      {label}
    </span>
  );
}

function StatusChip({ status }: { status: string }) {
  const isOngoing = status === 'ongoing';
  return (
    <span
      className={
        isOngoing
          ? 'text-xs rounded-full px-2 py-0.5 bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 border border-emerald-500/40'
          : 'text-xs rounded-full px-2 py-0.5 bg-zinc-500/15 text-zinc-700 dark:text-zinc-300 border border-zinc-500/30'
      }
    >
      {isOngoing ? 'ao vivo' : 'finalizada'}
    </span>
  );
}
